// OCR export — TXT, DOCX, XLSX, searchable PDF, ZIP, JSON.
// Exposes: window.OcrExport = { txt, docx, xlsx, searchablePdf, zipAll, detectTables, toJson }
(function () {
  'use strict';

  function download(blob, name) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = name; a.style.display = 'none';
    document.body.appendChild(a); a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 600);
  }

  function safeName(base, ext) {
    return (base || 'ocr-result').replace(/[^\w\-.]+/g, '_').slice(0, 80) + '.' + ext;
  }

  function resultText(result) {
    if (result.text != null) return result.text;
    if (!result.words) return '';
    return result.words.map(w => w.text).join(' ');
  }

  function txt(results) {
    const text = results.map(r => resultText(r)).join('\n\n');
    return new Blob([text], { type: 'text/plain;charset=utf-8' });
  }

  function markdown(results) {
    const parts = results.map(r => {
      const title = r.fileName || 'Page';
      return `## ${title}\n\n${resultText(r)}`;
    });
    return new Blob([parts.join('\n\n---\n\n')], { type: 'text/markdown;charset=utf-8' });
  }

  function toJson(results) {
    const payload = results.map(r => ({
      fileName: r.fileName,
      pageNumber: r.pageNumber || null,
      language: r.language || null,
      confidence: r.confidence != null ? Math.round(r.confidence) : null,
      text: resultText(r),
      words: (r.words || []).map(w => ({
        text: w.text,
        confidence: Math.round(w.confidence || 0),
        bbox: w.bbox || null,
      })),
      parsed: r.parsed || null,
    }));
    return new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  }

  // Group words into rows and columns; returns { rows: [[cell, cell, ...]], isTable: bool }
  function detectTables(words) {
    if (!words || words.length < 6) return { rows: null, isTable: false };
    const sorted = words.slice().sort((a, b) => {
      const ay = a.bbox ? a.bbox.y0 : 0, by = b.bbox ? b.bbox.y0 : 0;
      if (Math.abs(ay - by) > 6) return ay - by;
      const ax = a.bbox ? a.bbox.x0 : 0, bx = b.bbox ? b.bbox.x0 : 0;
      return ax - bx;
    });
    const rows = [];
    let current = [];
    let currentY = -Infinity;
    const rowTolerance = 8;
    for (const w of sorted) {
      if (!w.bbox) continue;
      const y = (w.bbox.y0 + w.bbox.y1) / 2;
      if (current.length === 0) { current.push(w); currentY = y; continue; }
      if (Math.abs(y - currentY) <= rowTolerance) {
        current.push(w);
      } else {
        rows.push(current);
        current = [w];
        currentY = y;
      }
    }
    if (current.length) rows.push(current);

    // Determine typical column count by computing x-gaps per row
    const cellRows = rows.map(row => {
      row.sort((a, b) => a.bbox.x0 - b.bbox.x0);
      if (row.length < 2) return row.map(w => w.text);
      const gaps = [];
      for (let i = 1; i < row.length; i++) {
        gaps.push(row[i].bbox.x0 - row[i - 1].bbox.x1);
      }
      const sortedGaps = gaps.slice().sort((a, b) => a - b);
      const median = sortedGaps[Math.floor(sortedGaps.length / 2)] || 0;
      const threshold = Math.max(median * 2.2, 20);
      const cells = [row[0].text];
      for (let i = 1; i < row.length; i++) {
        const gap = row[i].bbox.x0 - row[i - 1].bbox.x1;
        if (gap >= threshold) cells.push(row[i].text);
        else cells[cells.length - 1] += ' ' + row[i].text;
      }
      return cells;
    });

    // Count rows per column-count; if ≥3 rows share the same count, treat as table
    const counts = {};
    cellRows.forEach(r => { counts[r.length] = (counts[r.length] || 0) + 1; });
    const dominant = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
    const isTable = dominant && parseInt(dominant[0]) >= 2 && dominant[1] >= 3;
    return { rows: cellRows, isTable: !!isTable, columnCount: dominant ? parseInt(dominant[0]) : 1 };
  }

  // Group words into visual lines (same y-band). Returns [{lineHeight, text, words}].
  function groupLines(words) {
    if (!words || !words.length) return [];
    const valid = words.filter(w => w.bbox);
    if (!valid.length) return [];
    const sorted = valid.slice().sort((a, b) => a.bbox.y0 - b.bbox.y0);
    const lines = [];
    let current = [];
    let currentTop = sorted[0].bbox.y0;
    const lineTol = 8;
    for (const w of sorted) {
      if (current.length === 0 || Math.abs(w.bbox.y0 - currentTop) <= lineTol) {
        current.push(w);
        currentTop = current.reduce((a, b) => a + b.bbox.y0, 0) / current.length;
      } else {
        lines.push(current);
        current = [w];
        currentTop = w.bbox.y0;
      }
    }
    if (current.length) lines.push(current);

    return lines.map(ws => {
      ws.sort((a, b) => a.bbox.x0 - b.bbox.x0);
      const heights = ws.map(w => w.bbox.y1 - w.bbox.y0).filter(h => h > 0);
      const lineHeight = heights.length
        ? heights.reduce((a, b) => a + b, 0) / heights.length
        : 0;
      return { lineHeight, text: ws.map(w => w.text).join(' '), words: ws };
    });
  }

  // Median line-height across all results — used to classify headings
  function medianLineHeight(allResults) {
    const allHeights = [];
    for (const r of allResults) {
      const lines = groupLines(r.words || []);
      lines.forEach(l => l.lineHeight > 0 && allHeights.push(l.lineHeight));
    }
    if (!allHeights.length) return 0;
    allHeights.sort((a, b) => a - b);
    return allHeights[Math.floor(allHeights.length / 2)];
  }

  // Build DOCX. opts.mode === 'layout' enables heading detection; tables auto-detected.
  function docx(results, opts) {
    if (!window.docx) throw new Error('docx library not loaded');
    opts = opts || {};
    const mode = opts.mode || 'plain';
    const D = window.docx;
    const median = mode === 'layout' ? medianLineHeight(results) : 0;
    const children = [];

    results.forEach((r, idx) => {
      // Per-file title (filename) as Heading1 when batch
      if (results.length > 1 && r.fileName) {
        children.push(new D.Paragraph({
          text: r.fileName.replace(/\.\w+$/, ''),
          heading: D.HeadingLevel.HEADING_1,
        }));
      }

      const tableInfo = detectTables(r.words || []);
      if (tableInfo.isTable && tableInfo.rows && tableInfo.rows.length >= 2) {
        // Render as DOCX table
        const colCount = tableInfo.columnCount;
        const tableRows = tableInfo.rows
          .filter(row => row.length === colCount || row.length >= 2)
          .map((row, rowIdx) => new D.TableRow({
            children: Array.from({ length: colCount }).map((_, ci) => new D.TableCell({
              children: [new D.Paragraph({
                children: [new D.TextRun({
                  text: row[ci] || '',
                  bold: rowIdx === 0,
                })],
              })],
            })),
          }));
        children.push(new D.Table({
          rows: tableRows,
          width: { size: 100, type: D.WidthType.PERCENTAGE },
        }));
        children.push(new D.Paragraph({ children: [new D.TextRun('')] }));
      } else if (mode === 'layout' && median > 0) {
        // Layout mode with heading detection
        const lines = groupLines(r.words || []);
        for (const line of lines) {
          if (!line.text.trim()) continue;
          let heading = null;
          if (line.lineHeight > median * 1.6) heading = D.HeadingLevel.HEADING_1;
          else if (line.lineHeight > median * 1.3) heading = D.HeadingLevel.HEADING_2;
          if (heading) {
            children.push(new D.Paragraph({ text: line.text, heading }));
          } else {
            children.push(new D.Paragraph({ children: [new D.TextRun(line.text)] }));
          }
        }
      } else {
        // Plain paragraphs from text
        const text = resultText(r);
        const lines = text.split(/\r?\n/);
        for (const line of lines) {
          children.push(new D.Paragraph({ children: [new D.TextRun(line || ' ')] }));
        }
      }

      if (idx < results.length - 1) {
        children.push(new D.Paragraph({ children: [new D.PageBreak()] }));
      }
    });

    const doc = new D.Document({ sections: [{ children }] });
    return D.Packer.toBlob(doc);
  }

  function xlsx(results, opts) {
    if (!window.XLSX) throw new Error('SheetJS not loaded');
    opts = opts || {};
    const wb = window.XLSX.utils.book_new();

    // Receipt-mode: extra summary sheet at the front
    const hasParsed = results.some(r => r.parsed && (r.parsed.total || r.parsed.date || r.parsed.vendor));
    if (hasParsed) {
      const aoa = [['File', 'Vendor', 'Date', 'Total', 'Currency']];
      results.forEach(r => {
        const p = r.parsed || {};
        aoa.push([r.fileName || '', p.vendor || '', p.date || '', p.total || '', p.currency || '']);
      });
      const ws = window.XLSX.utils.aoa_to_sheet(aoa);
      window.XLSX.utils.book_append_sheet(wb, ws, 'Summary');
    }

    results.forEach((r, idx) => {
      const sheetName = (r.fileName || ('Sheet ' + (idx + 1))).replace(/[\\\/\*\[\]\?:]+/g, '_').slice(0, 31);
      let aoa;
      const tables = detectTables(r.words || []);
      if (tables.isTable && tables.rows) {
        aoa = tables.rows;
      } else {
        aoa = resultText(r).split(/\r?\n/).map(line => [line]);
      }
      const ws = window.XLSX.utils.aoa_to_sheet(aoa);
      window.XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });
    const out = window.XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    return new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  }

  // Searchable PDF with A4 page normalisation.
  // Each page is fit (preserving aspect) into 595×842 pt (A4 portrait) or
  // 842×595 (landscape) depending on image aspect. Word coordinates are
  // scaled to match the rendered image rectangle on the page.
  async function searchablePdf(results, opts) {
    opts = opts || {};
    const jspdfGlobal = window.jspdf || window.jsPDF;
    if (!jspdfGlobal) throw new Error('jsPDF not loaded');
    const { jsPDF } = jspdfGlobal;

    const A4_W = 595.28; // points
    const A4_H = 841.89;

    const doc = new jsPDF({ unit: 'pt', format: 'a4', compress: true });
    let first = true;
    for (const r of results) {
      // The embedded picture is the user's ORIGINAL page (post-rotation), never
      // the preprocessed one — a binarized/inverted ghost in the "searchable PDF"
      // was the pre-fix behaviour and looked broken to users. Word coordinates
      // stay in processed space; when preprocessing changed geometry (autocrop/
      // deskew) the invisible layer may drift by the cropped margin — Ctrl+F
      // still finds every word. Falls back to the processed image for legacy
      // results (session-resume from before this fix).
      const imgUrl = r.originalDataUrl || r.imageDataUrl;
      if (!imgUrl) continue;
      const imgW = (r.originalDataUrl ? r.originalWidth : r.imageWidth) || r.imageWidth || 2000;
      const imgH = (r.originalDataUrl ? r.originalHeight : r.imageHeight) || r.imageHeight || 2800;
      const wordW = r.imageWidth || imgW;
      const wordH = r.imageHeight || imgH;
      const portraitImg = imgH >= imgW;
      const pageW = portraitImg ? A4_W : A4_H;
      const pageH = portraitImg ? A4_H : A4_W;
      if (!first) doc.addPage([pageW, pageH], portraitImg ? 'p' : 'l');
      else { doc.deletePage(1); doc.addPage([pageW, pageH], portraitImg ? 'p' : 'l'); }
      first = false;

      // Aspect-fit image into page
      const sx = pageW / imgW;
      const sy = pageH / imgH;
      const s = Math.min(sx, sy);
      const drawW = imgW * s;
      const drawH = imgH * s;
      const offX = (pageW - drawW) / 2;
      const offY = (pageH - drawH) / 2;
      // word-space → page-space (processed dims may differ from original dims)
      const ws = Math.min(drawW / wordW, drawH / wordH);

      doc.addImage(imgUrl, 'JPEG', offX, offY, drawW, drawH, undefined, 'FAST');

      // Invisible text layer — scale word coordinates and shift by (offX, offY)
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(1);
      if (r.words && r.words.length) {
        doc.saveGraphicsState && doc.saveGraphicsState();
        try { doc.internal.write('3 Tr'); } catch (e) {} // Set rendering mode = invisible
        for (const w of r.words) {
          if (!w.bbox || !w.text) continue;
          const fontSize = Math.max(1, (w.bbox.y1 - w.bbox.y0) * ws * 0.9);
          doc.setFontSize(fontSize);
          const x = offX + w.bbox.x0 * ws;
          const y = offY + w.bbox.y1 * ws - (fontSize * 0.15);
          try { doc.text(w.text, x, y, { renderingMode: 'invisible' }); }
          catch (_) { try { doc.text(w.text, x, y); } catch (_) {} }
        }
        doc.restoreGraphicsState && doc.restoreGraphicsState();
      }
    }
    return doc.output('blob');
  }

  async function zipAll(named) {
    if (!window.JSZip) throw new Error('JSZip not loaded');
    const zip = new window.JSZip();
    for (const { name, blob } of named) {
      zip.file(name, blob);
    }
    return await zip.generateAsync({ type: 'blob' });
  }

  window.OcrExport = {
    txt, markdown, toJson, docx, xlsx, searchablePdf, zipAll,
    detectTables, groupLines, medianLineHeight, download, safeName,
  };
})();
